import React, { useEffect, useMemo } from 'react';
import DataSourceContext from './DataSourceContext';
import {
  DataSourceProps, DataSourceState, DataSourceStatePayload, ServerDataSourceProviderProps,
  SortDefinition,
} from './types';
import { dataReducer, initialState } from './reducer';
import {
  nextPage,
  prevPage,
  goToPage,
  setData,
  setIsLoading,
  setError,
  setSearch,
  setPageSize,
  setSortBy,
} from './reducer/actions';

export const ServerDataSource: React.FC<ServerDataSourceProviderProps & Partial<DataSourceState>> = ({
  children,
  fetchData,
  ...overrideInitialState
}) => {
  const [state, dispatch] = React.useReducer(dataReducer, { ...initialState, ...overrideInitialState });
  const {
    currentPage, pageSize, searchTerm, sortBy,
  } = state;
  const [refetchTrigger, setRefetchTrigger] = React.useState<number>(0);

  useEffect(() => {
    if (fetchData) {
      dispatch(setIsLoading(true));
      fetchData({
        page: currentPage, pageSize, search: state.searchTerm, sortState: sortBy,
      })
        .then((data: DataSourceStatePayload) => {
          dispatch(setData(data));
        })
        .catch((error) => {
          dispatch(setError(error.message));
        });
    }
  }, [currentPage, pageSize, searchTerm, sortBy, refetchTrigger]);

  const value: DataSourceProps = useMemo(() => ({
    ...state,
    nextPage: () => dispatch(nextPage()),
    prevPage: () => dispatch(prevPage()),
    goToPage: (pageNumber: number) => dispatch(goToPage(pageNumber)),
    setError: (error: string) => dispatch(setError(error)),
    setData: (data: DataSourceStatePayload) => dispatch(setData(data)),
    search: (search: string) => dispatch(setSearch(search)),
    sort: (val: SortDefinition) => dispatch(setSortBy(val)),
    setPageSize: (size: number) => dispatch(setPageSize(size)),
    refetch: () => setRefetchTrigger((prev: number) => prev + 1),
  }), [state]);

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
};
